import type { ThreadEntry } from "../../../api/threads/liveMessages";
import type { EntriesPage } from "../../../api/threads/threadDecoders";
import { reportUiError, toUiError, type UiError } from "../../../errors/uiError";

/** How many entries before the log one read of earlier entries asks for. */
export const EARLIER_PAGE = 100;

/** What reading earlier entries needs of the live client, which owns each Thread's log (`threadLiveClient.ts`). */
export interface EarlierHost {
  /** Where the Thread's log starts now; `null` once nobody watches it. */
  readonly baseOf: (threadId: string) => number | null;
  readonly read: (threadId: string, after: number, limit: number) => Promise<EntriesPage>;
  /** Puts a block of entries before the log; where it starts after that, `null` once nobody watches it. */
  readonly prepend: (threadId: string, block: readonly ThreadEntry[]) => number | null;
  /** A read of earlier entries started, or ended, with the error it ended on. */
  readonly loading: (threadId: string, loading: boolean, error: UiError | null) => void;
}

/**
 * A Thread's earlier entries, read in for whoever watches it (WD-4). The earliest start any
 * watcher asks for is kept, also while a read runs (Sol c2 C2-03), and read in adjoining
 * blocks of at most `EARLIER_PAGE` entries, each read forward page by page as the server serves
 * them, whatever its page size (Sol c3 C3-01). A block that doesn't adjoin the log is a broken
 * server order, which the log records: the read stops there.
 */
export class EarlierReads {
  /** By Thread, the earliest start wanted while its read runs. */
  private readonly wanted = new Map<string, number>();

  constructor(private readonly host: EarlierHost) {}

  /** The page before the first entry held, or with `from`, every entry after it. */
  async load(threadId: string, from: number | null): Promise<void> {
    const base = this.host.baseOf(threadId);
    if (base === null || base === 0) return;
    const wanted = Math.max(0, from ?? base - EARLIER_PAGE);
    const running = this.wanted.has(threadId);
    this.wanted.set(threadId, Math.min(this.wanted.get(threadId) ?? wanted, wanted));
    if (running) return;
    this.host.loading(threadId, true, null);
    let error: UiError | null = null;
    try {
      await this.drain(threadId);
    } catch (caught) {
      error = toUiError(caught);
      reportUiError(error);
    } finally {
      this.wanted.delete(threadId);
    }
    this.host.loading(threadId, false, error);
  }

  private async drain(threadId: string): Promise<void> {
    for (;;) {
      const base = this.host.baseOf(threadId);
      const wanted = this.wanted.get(threadId);
      if (base === null || wanted === undefined || base <= wanted) return;
      const block = await this.entriesBetween(threadId, Math.max(wanted, base - EARLIER_PAGE), base);
      const moved = this.host.prepend(threadId, block);
      if (moved === null || moved === base) return;
    }
  }

  /** The entries after `after` up to `base`, read forward; fewer when the server ends early. */
  private async entriesBetween(threadId: string, after: number, base: number): Promise<ThreadEntry[]> {
    const block: ThreadEntry[] = [];
    let cursor = after;
    while (cursor < base) {
      const page = await this.host.read(threadId, cursor, base - cursor);
      const read = page.entries.filter((entry) => entry.number > cursor && entry.number <= base);
      const last = read[read.length - 1];
      if (last === undefined) break;
      block.push(...read);
      cursor = last.number;
    }
    return block;
  }
}
